/**
 * PDFAnnotationViewer
 *
 * Renders a PDF using pdfjs-dist canvas rendering, with an interactive
 * annotation overlay layer on top. Annotations are stored on the backend;
 * the original PDF file is never modified.
 *
 * Coordinate system: annotations use 0-1000 normalised units relative to
 * the rendered page canvas. This decouples annotation positions from actual
 * PDF point dimensions and display zoom level.
 */
import { useEffect, useRef, useState, useCallback } from 'react';
import { Loader2, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth/AuthProvider';
import { toast } from 'sonner';
import {
  AnnotationToolbar,
  type AnnotationMode,
  type StampType,
  STAMP_LABELS,
} from './AnnotationToolbar';
import { SendForReviewDialog } from './SendForReviewDialog';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface Annotation {
  id: string;
  submittal_id: string;
  file_id: string;
  page_number: number;
  annotation_type: 'text' | 'stamp' | 'freehand';
  data: Record<string, any>;
  created_by: string;
  created_at: string;
  updated_at: string;
}

interface PendingTextInput {
  x: number; // canvas-relative pixels
  y: number;
  pageNumber: number;
}

interface PDFAnnotationViewerProps {
  submittalId: string;
  fileId: string;
  fileName: string;
  projectId: string;
  submittalNumber: string;
  /** Download URL or signed URL for the PDF */
  pdfUrl: string;
  onClose?: () => void;
}

// ---------------------------------------------------------------------------
// Stamp rendering helpers for canvas overlay
// ---------------------------------------------------------------------------

const STAMP_COLORS: Record<StampType, string> = {
  approved: '#008a00',
  approved_as_noted: '#0074cd',
  revise_resubmit: '#f28b00',
  rejected: '#cc0000',
};

const COORD_SCALE = 1000; // normalised coordinate space

function normToCanvas(n: number, dim: number): number {
  return (n / COORD_SCALE) * dim;
}

function canvasToNorm(c: number, dim: number): number {
  return (c / dim) * COORD_SCALE;
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function PDFAnnotationViewer({
  submittalId,
  fileId,
  fileName,
  projectId,
  submittalNumber,
  pdfUrl,
  onClose,
}: PDFAnnotationViewerProps) {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  // PDF rendering state
  const [pdfDocument, setPdfDocument] = useState<any | null>(null);
  const [numPages, setNumPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pdfLoading, setPdfLoading] = useState(true);
  const [pdfError, setPdfError] = useState<string | null>(null);
  const [renderScale, setRenderScale] = useState(1.5);

  // Annotation interaction state
  const [mode, setMode] = useState<AnnotationMode>('pointer');
  const [stampType, setStampType] = useState<StampType>('approved');
  const [freehandColor, setFreehandColor] = useState('#e63946');
  const [drawing, setDrawing] = useState(false);
  const [currentPath, setCurrentPath] = useState<[number, number][]>([]);
  const [pendingText, setPendingText] = useState<PendingTextInput | null>(null);
  const [textInputValue, setTextInputValue] = useState('');
  const [exporting, setExporting] = useState(false);
  const [sendDialogOpen, setSendDialogOpen] = useState(false);

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const overlayRef = useRef<HTMLCanvasElement>(null);
  const textInputRef = useRef<HTMLInputElement>(null);

  // ---------------------------------------------------------------------------
  // Load PDF
  // ---------------------------------------------------------------------------

  useEffect(() => {
    let cancelled = false;

    async function loadPdf() {
      setPdfLoading(true);
      setPdfError(null);

      try {
        // Dynamic import to avoid SSR issues and allow code-splitting
        const pdfjsLib = await import('pdfjs-dist');

        // Configure worker — Vite resolves the worker URL at build time
        pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
          'pdfjs-dist/build/pdf.worker.min.mjs',
          import.meta.url
        ).toString();

        const loadingTask = pdfjsLib.getDocument({
          url: pdfUrl,
          withCredentials: false,
        });

        const doc = await loadingTask.promise;

        if (!cancelled) {
          setPdfDocument(doc);
          setNumPages(doc.numPages);
          setCurrentPage(1);
          setPdfLoading(false);
        }
      } catch (err: any) {
        if (!cancelled) {
          setPdfError(err.message || 'Failed to load PDF');
          setPdfLoading(false);
        }
      }
    }

    loadPdf();
    return () => {
      cancelled = true;
    };
  }, [pdfUrl]);

  // ---------------------------------------------------------------------------
  // Render PDF page to canvas
  // ---------------------------------------------------------------------------

  useEffect(() => {
    if (!pdfDocument || !canvasRef.current) return;

    let cancelled = false;

    async function renderPage() {
      const page = await pdfDocument.getPage(currentPage);
      if (cancelled) return;

      const viewport = page.getViewport({ scale: renderScale });

      const canvas = canvasRef.current!;
      const ctx = canvas.getContext('2d')!;
      canvas.width = viewport.width;
      canvas.height = viewport.height;

      // Also size the overlay canvas
      if (overlayRef.current) {
        overlayRef.current.width = viewport.width;
        overlayRef.current.height = viewport.height;
      }

      await page.render({ canvasContext: ctx, viewport }).promise;
      if (cancelled) return;

      // Redraw saved annotations on overlay
      redrawAnnotations();
    }

    renderPage();
    return () => {
      cancelled = true;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pdfDocument, currentPage, renderScale]);

  // ---------------------------------------------------------------------------
  // Fetch persisted annotations
  // ---------------------------------------------------------------------------

  const { data: annotationsData } = useQuery({
    queryKey: ['submittal-annotations', submittalId, fileId],
    queryFn: () => api.submittals.annotations.list(submittalId, fileId),
    enabled: !!submittalId && !!fileId,
  });

  const savedAnnotations: Annotation[] = (annotationsData?.annotations || []);

  // Redraw annotations for the current page onto the overlay canvas
  const redrawAnnotations = useCallback(() => {
    const overlay = overlayRef.current;
    if (!overlay) return;

    const ctx = overlay.getContext('2d')!;
    ctx.clearRect(0, 0, overlay.width, overlay.height);

    const pageAnnotations = savedAnnotations.filter(
      (a) => a.page_number === currentPage
    );

    for (const ann of pageAnnotations) {
      drawAnnotationOnCanvas(ctx, ann, overlay.width, overlay.height);
    }
  }, [savedAnnotations, currentPage]);

  // Re-run redraw when annotations or page changes
  useEffect(() => {
    redrawAnnotations();
  }, [redrawAnnotations]);

  // ---------------------------------------------------------------------------
  // Annotation draw helpers
  // ---------------------------------------------------------------------------

  function drawAnnotationOnCanvas(
    ctx: CanvasRenderingContext2D,
    ann: Annotation,
    w: number,
    h: number
  ) {
    const { annotation_type: type, data } = ann;

    if (type === 'text') {
      const x = normToCanvas(data.x || 0, w);
      const y = normToCanvas(data.y || 0, h);
      const content = data.content || '';
      const fontSize = data.fontSize || 13;
      const color = data.color || '#1a1aff';

      ctx.font = `${fontSize}px Arial, sans-serif`;
      const metrics = ctx.measureText(content);
      const textW = metrics.width;
      const textH = fontSize;
      const pad = 4;

      // Background
      ctx.fillStyle = 'rgba(255, 255, 200, 0.88)';
      ctx.fillRect(x - pad, y - textH - pad, textW + pad * 2, textH + pad * 2);
      ctx.strokeStyle = color;
      ctx.lineWidth = 1;
      ctx.strokeRect(x - pad, y - textH - pad, textW + pad * 2, textH + pad * 2);

      // Text
      ctx.fillStyle = color;
      ctx.fillText(content, x, y);
    } else if (type === 'stamp') {
      const x = normToCanvas(data.x || 0, w);
      const y = normToCanvas(data.y || 0, h);
      const stampLabel = STAMP_LABELS[data.stamp_type as StampType] || data.stamp_type || 'STAMP';
      const color = STAMP_COLORS[data.stamp_type as StampType] || '#555555';
      const boxW = normToCanvas(data.width || 160, w);
      const boxH = 52;

      ctx.strokeStyle = color;
      ctx.lineWidth = 2;
      ctx.strokeRect(x, y - boxH, boxW, boxH);

      // Divider
      ctx.strokeStyle = color;
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(x + 4, y - 28);
      ctx.lineTo(x + boxW - 4, y - 28);
      ctx.stroke();

      // Label
      ctx.fillStyle = color;
      ctx.font = 'bold 12px Arial, sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText(stampLabel, x + boxW / 2, y - 32);

      // Architect name
      if (data.architect_name) {
        ctx.fillStyle = '#444';
        ctx.font = '9px Arial, sans-serif';
        ctx.fillText(data.architect_name, x + boxW / 2, y - 18);
      }

      // Date
      if (data.stamp_date) {
        ctx.fillStyle = '#666';
        ctx.font = '8px Arial, sans-serif';
        ctx.fillText(data.stamp_date, x + boxW / 2, y - 8);
      }

      ctx.textAlign = 'left';
    } else if (type === 'freehand') {
      const points: [number, number][] = data.points || [];
      if (points.length < 2) return;

      const color = data.color || '#e63946';
      const strokeWidth = data.strokeWidth || 2;

      ctx.strokeStyle = color;
      ctx.lineWidth = strokeWidth;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.beginPath();
      ctx.moveTo(normToCanvas(points[0][0], w), normToCanvas(points[0][1], h));
      for (let i = 1; i < points.length; i++) {
        ctx.lineTo(normToCanvas(points[i][0], w), normToCanvas(points[i][1], h));
      }
      ctx.stroke();
    }
  }

  // ---------------------------------------------------------------------------
  // Mutations
  // ---------------------------------------------------------------------------

  const createAnnotation = useMutation({
    mutationFn: (annData: Parameters<typeof api.submittals.annotations.create>[1]) =>
      api.submittals.annotations.create(submittalId, annData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['submittal-annotations', submittalId, fileId] });
    },
    onError: (err: any) => {
      toast.error(err.message || 'Failed to save annotation');
    },
  });

  const deleteAnnotation = useMutation({
    mutationFn: (annotationId: string) =>
      api.submittals.annotations.delete(submittalId, annotationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['submittal-annotations', submittalId, fileId] });
      toast.success('Annotation deleted');
    },
    onError: (err: any) => {
      toast.error(err.message || 'Failed to delete annotation');
    },
  });

  // Undo = delete the most recent annotation on this page
  const handleUndo = () => {
    const pageAnnotations = savedAnnotations
      .filter((a) => a.page_number === currentPage)
      .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

    if (pageAnnotations.length === 0) return;
    deleteAnnotation.mutate(pageAnnotations[0].id);
  };

  // ---------------------------------------------------------------------------
  // Canvas event handlers
  // ---------------------------------------------------------------------------

  function getCanvasCoords(e: React.MouseEvent<HTMLCanvasElement>): { x: number; y: number } {
    const canvas = overlayRef.current!;
    const rect = canvas.getBoundingClientRect();
    return {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    };
  }

  function handleCanvasClick(e: React.MouseEvent<HTMLCanvasElement>) {
    if (mode === 'pointer') return;
    if (mode === 'freehand') return; // handled by mousedown/move/up

    const { x, y } = getCanvasCoords(e);
    const overlay = overlayRef.current!;
    const normX = canvasToNorm(x, overlay.width);
    const normY = canvasToNorm(y, overlay.height);

    if (mode === 'text') {
      // Place a text input at click position
      setPendingText({ x, y, pageNumber: currentPage });
      setTextInputValue('');
      setTimeout(() => textInputRef.current?.focus(), 50);
      return;
    }

    if (mode === 'stamp') {
      const architectName = user?.name || user?.email || 'Architect';
      const stampDate = new Date().toLocaleDateString('en-CA'); // YYYY-MM-DD

      createAnnotation.mutate({
        file_id: fileId,
        page_number: currentPage,
        annotation_type: 'stamp',
        data: {
          x: normX,
          y: normY,
          stamp_type: stampType,
          architect_name: architectName,
          stamp_date: stampDate,
          width: 160,
        },
      });
    }
  }

  function handleMouseDown(e: React.MouseEvent<HTMLCanvasElement>) {
    if (mode !== 'freehand') return;
    setDrawing(true);
    const { x, y } = getCanvasCoords(e);
    const overlay = overlayRef.current!;
    setCurrentPath([[
      canvasToNorm(x, overlay.width),
      canvasToNorm(y, overlay.height),
    ]]);
  }

  function handleMouseMove(e: React.MouseEvent<HTMLCanvasElement>) {
    if (!drawing || mode !== 'freehand') return;
    const { x, y } = getCanvasCoords(e);
    const overlay = overlayRef.current!;

    setCurrentPath((prev) => [
      ...prev,
      [canvasToNorm(x, overlay.width), canvasToNorm(y, overlay.height)],
    ]);

    // Live preview on overlay canvas
    const ctx = overlay.getContext('2d')!;
    redrawAnnotations();
    if (currentPath.length >= 1) {
      ctx.strokeStyle = freehandColor;
      ctx.lineWidth = 2;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.beginPath();
      ctx.moveTo(normToCanvas(currentPath[0][0], overlay.width), normToCanvas(currentPath[0][1], overlay.height));
      for (let i = 1; i < currentPath.length; i++) {
        ctx.lineTo(normToCanvas(currentPath[i][0], overlay.width), normToCanvas(currentPath[i][1], overlay.height));
      }
      ctx.lineTo(x, y);
      ctx.stroke();
    }
  }

  function handleMouseUp() {
    if (!drawing || mode !== 'freehand') return;
    setDrawing(false);

    if (currentPath.length >= 2) {
      createAnnotation.mutate({
        file_id: fileId,
        page_number: currentPage,
        annotation_type: 'freehand',
        data: {
          points: currentPath,
          color: freehandColor,
          strokeWidth: 2,
        },
      });
    }
    setCurrentPath([]);
  }

  function handleTextSubmit() {
    if (!pendingText || !textInputValue.trim()) {
      setPendingText(null);
      return;
    }

    const overlay = overlayRef.current!;
    const normX = canvasToNorm(pendingText.x, overlay.width);
    const normY = canvasToNorm(pendingText.y, overlay.height);

    createAnnotation.mutate({
      file_id: fileId,
      page_number: currentPage,
      annotation_type: 'text',
      data: {
        x: normX,
        y: normY,
        content: textInputValue.trim(),
        fontSize: 13,
        color: '#1a1aff',
      },
    });

    setPendingText(null);
    setTextInputValue('');
  }

  // ---------------------------------------------------------------------------
  // Export / Send
  // ---------------------------------------------------------------------------

  const handleExportPdf = async () => {
    setExporting(true);
    try {
      await api.submittals.annotations.exportFlatPdf(submittalId, fileId, fileName);
      toast.success('Annotated PDF downloaded');
    } catch (err: any) {
      toast.error(err.message || 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Cursor styling based on mode
  // ---------------------------------------------------------------------------

  const cursorStyle: Record<AnnotationMode, string> = {
    pointer: 'default',
    text: 'text',
    stamp: 'crosshair',
    freehand: 'crosshair',
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <div className="flex flex-col h-full bg-muted/20">
      {/* Toolbar */}
      <AnnotationToolbar
        mode={mode}
        stampType={stampType}
        freehandColor={freehandColor}
        onModeChange={setMode}
        onStampTypeChange={setStampType}
        onColorChange={setFreehandColor}
        onUndo={handleUndo}
        onExportPdf={handleExportPdf}
        onSendForReview={() => setSendDialogOpen(true)}
        canUndo={savedAnnotations.some((a) => a.page_number === currentPage)}
        exporting={exporting}
      />

      {/* Page controls */}
      {numPages > 1 && (
        <div className="flex items-center justify-center gap-3 py-1.5 bg-background border-b text-sm">
          <Button
            variant="ghost"
            size="sm"
            className="h-6 px-2 text-xs"
            disabled={currentPage <= 1}
            onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
          >
            ‹ Prev
          </Button>
          <span className="text-muted-foreground">
            Page {currentPage} of {numPages}
          </span>
          <Button
            variant="ghost"
            size="sm"
            className="h-6 px-2 text-xs"
            disabled={currentPage >= numPages}
            onClick={() => setCurrentPage((p) => Math.min(numPages, p + 1))}
          >
            Next ›
          </Button>
          <div className="flex items-center gap-1 ml-2">
            <Button
              variant="ghost"
              size="sm"
              className="h-6 px-2 text-xs"
              onClick={() => setRenderScale((s) => Math.max(0.5, s - 0.25))}
            >
              −
            </Button>
            <span className="text-xs text-muted-foreground w-12 text-center">
              {Math.round(renderScale * 100)}%
            </span>
            <Button
              variant="ghost"
              size="sm"
              className="h-6 px-2 text-xs"
              onClick={() => setRenderScale((s) => Math.min(3, s + 0.25))}
            >
              +
            </Button>
          </div>
          {onClose && (
            <Button
              variant="ghost"
              size="sm"
              className="h-6 w-6 p-0 ml-2"
              onClick={onClose}
              title="Close viewer"
            >
              <X className="w-3.5 h-3.5" />
            </Button>
          )}
        </div>
      )}

      {/* PDF canvas area */}
      <div className="flex-1 overflow-auto flex justify-center p-4">
        {pdfLoading && (
          <div className="flex items-center gap-2 text-muted-foreground py-12">
            <Loader2 className="w-5 h-5 animate-spin" />
            <span>Loading PDF…</span>
          </div>
        )}

        {pdfError && (
          <div className="py-12 text-destructive text-sm text-center">
            <p>Failed to load PDF: {pdfError}</p>
          </div>
        )}

        {!pdfLoading && !pdfError && (
          <div className="relative inline-block shadow-lg">
            {/* PDF canvas */}
            <canvas ref={canvasRef} className="block" />

            {/* Annotation overlay canvas */}
            <canvas
              ref={overlayRef}
              className="absolute inset-0"
              style={{
                cursor: cursorStyle[mode],
                opacity: 0.9,
              }}
              onClick={handleCanvasClick}
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseUp}
            />

            {/* Inline text input for text annotations */}
            {pendingText && (
              <div
                className="absolute"
                style={{
                  left: pendingText.x,
                  top: pendingText.y - 28,
                  zIndex: 10,
                }}
              >
                <input
                  ref={textInputRef}
                  type="text"
                  value={textInputValue}
                  onChange={(e) => setTextInputValue(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleTextSubmit();
                    if (e.key === 'Escape') {
                      setPendingText(null);
                      setTextInputValue('');
                    }
                  }}
                  onBlur={handleTextSubmit}
                  placeholder="Type comment…"
                  className="border border-blue-400 rounded px-1 py-0.5 text-sm bg-yellow-50 shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-400 min-w-32"
                  style={{ fontSize: 13 }}
                />
              </div>
            )}
          </div>
        )}
      </div>

      {/* Send for Review dialog */}
      <SendForReviewDialog
        open={sendDialogOpen}
        onClose={() => setSendDialogOpen(false)}
        submittalId={submittalId}
        fileId={fileId}
        projectId={projectId}
        submittalNumber={submittalNumber}
      />
    </div>
  );
}
